import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ExternalLink, Sparkles, Users, Target } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import IframePricing from "@/components/IframePricing";
import IframeLiveDemo from "@/components/IframeLiveDemo";
import IframeInstall from "@/components/IframeInstall";
import { PRODUCTS } from "@/lib/products";
import { useT } from "@/lib/i18n";

const POSITIONING_META = {
  b2c: { label: "product.pos.b2c", color: "var(--gv-pink)" },
  b2b: { label: "product.pos.b2b", color: "var(--gv-blue)" },
  education: { label: "product.pos.education", color: "#7a4dad" }, // distinct purple accent
};

const PositioningBadge = ({ positioning }) => {
  const t = useT();
  const meta = POSITIONING_META[positioning];
  if (!meta) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] text-[11px] uppercase tracking-widest font-medium"
      style={{ background: `${meta.color}15`, color: meta.color }}
      data-testid={`positioning-${positioning}`}
    >
      <Target size={11} /> {t(meta.label)}
    </span>
  );
};

const IdealForBlock = ({ slug, audience }) => {
  const t = useT();
  if (!audience?.length) return null;
  return (
    <div data-testid={`ideal-for-${slug}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">
        <Users size={12} /> {t("product.ideal_for")}
      </div>
      <ul className="flex flex-wrap gap-2">
        {audience.map((a) => (
          <li
            key={a}
            className="px-3 py-1.5 rounded-[4px] bg-[color:var(--gv-mist)] text-[13px] text-[color:var(--gv-ink)]/80 border border-[color:var(--gv-line)]"
          >
            {t(`audience.${a}`)}
          </li>
        ))}
      </ul>
    </div>
  );
};

const BenefitsBlock = ({ slug, benefits }) => {
  const t = useT();
  if (!benefits?.length) return null;
  return (
    <div data-testid={`benefits-${slug}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">
        <CheckCircle2 size={12} /> {t("product.key_benefits")}
      </div>
      <ul className="space-y-2.5">
        {benefits.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-[14px] text-[color:var(--gv-ink)]/85">
            <CheckCircle2 size={16} className="mt-0.5 text-[color:var(--gv-pink)] shrink-0" />
            <span>{t(`benefit.${b}`)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PositioningSection = ({ slug, positioning, audience, benefits }) => {
  if (!positioning && !audience && !benefits) return null;
  return (
    <section className="py-14 md:py-16 border-t border-[color:var(--gv-line)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
          <IdealForBlock slug={slug} audience={audience} />
          <BenefitsBlock slug={slug} benefits={benefits} />
        </div>
      </div>
    </section>
  );
};

const ExtendedSections = ({ product }) => {
  const t = useT();
  const s = product.sections || {};
  return (
    <>
      {s.forWho?.length > 0 && (
        <section className="py-16 md:py-20 bg-[color:var(--gv-mist)]">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="mb-10">
              <div className="eyebrow mb-3">{t("product.section.for_who")}</div>
              <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">{t(`product.${product.slug}.for_who.title`)}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {s.forWho.map((k) => (
                <div key={k} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid={`forwho-${k}`}>
                  <div className="font-display text-lg text-[color:var(--gv-ink)] mb-2">{t(`product.${product.slug}.for_who.${k}.title`)}</div>
                  <p className="text-sm text-[color:var(--gv-ink)]/70 leading-relaxed">{t(`product.${product.slug}.for_who.${k}.desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {s.features?.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="mb-10">
              <div className="eyebrow mb-3">{t("product.section.features")}</div>
              <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">{t(`product.${product.slug}.features.title`)}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              {s.features.map((k) => (
                <div key={k} className="flex gap-4" data-testid={`feature-${k}`}>
                  <div className="shrink-0 w-10 h-10 rounded-[4px] bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] flex items-center justify-center">
                    <CheckCircle2 size={18} />
                  </div>
                  <div>
                    <div className="font-display text-lg text-[color:var(--gv-ink)] mb-1.5">{t(`product.${product.slug}.feature.${k}.title`)}</div>
                    <p className="text-sm text-[color:var(--gv-ink)]/70 leading-relaxed">{t(`product.${product.slug}.feature.${k}.desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {s.useCases?.length > 0 && (
        <section className="py-16 md:py-20 bg-[color:var(--gv-mist)]">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="mb-10">
              <div className="eyebrow mb-3">{t("product.section.use_cases")}</div>
              <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">{t(`product.${product.slug}.use_cases.title`)}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {s.useCases.map((k, i) => (
                <div key={k} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid={`usecase-${k}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-blue)] mb-2">0{i + 1}</div>
                  <div className="font-display text-base text-[color:var(--gv-ink)] mb-1">{t(`product.${product.slug}.use.${k}.title`)}</div>
                  <p className="text-[13px] text-[color:var(--gv-ink)]/70 leading-relaxed">{t(`product.${product.slug}.use.${k}.desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {s.examples?.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="mb-8">
              <div className="eyebrow mb-3">{t("product.section.examples")}</div>
              <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">{t(`product.${product.slug}.examples.title`)}</h2>
            </div>
            <ul className="space-y-3">
              {s.examples.map((k) => (
                <li key={k} className="bg-white border border-[color:var(--gv-line)] rounded-[4px] px-5 py-3 text-[15px] text-[color:var(--gv-ink)] italic" data-testid={`example-${k}`}>
                  &ldquo;{t(`product.${product.slug}.example.${k}`)}&rdquo;
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {s.whyTitle && (
        <section className="py-16 md:py-20 bg-[color:var(--gv-ink)] text-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="font-display text-3xl md:text-5xl mb-4">{t(`product.${product.slug}.why.title`)}</h2>
            <p className="text-base md:text-lg text-white/80 leading-relaxed">{t(`product.${product.slug}.why.body`)}</p>
          </div>
        </section>
      )}

      {/* Iframe-specific sections: live demo, install guide, pricing module */}
      {product.slug === "iframe" && (
        <>
          <IframeLiveDemo />
          <IframeInstall />
          <IframePricing />
        </>
      )}
    </>
  );
};

const StatusPill = ({ status }) => {
  const t = useT();
  const isAvailable = status === "available";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] text-[11px] uppercase tracking-widest font-medium ${
      isAvailable
        ? "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]"
        : "bg-[color:var(--gv-blue)]/10 text-[color:var(--gv-blue)]"
    }`}>
      {isAvailable ? <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gv-pink)]" /> : <Sparkles size={11} />}
      {t(`product.status.${status}`)}
    </span>
  );
};

const ComingSoonForm = ({ slug }) => {
  const t = useT();
  // Strip "atlas-" / "{slug}-{plan}" suffixes so the subject preset matches.
  const baseSlug = slug.split("-")[0];
  return (
    <Link
      to={`/contact?subject=${baseSlug}-beta`}
      className="btn-primary"
      data-testid={`beta-cta-${slug}`}
    >
      {t("product.cta.become_beta")} <ArrowRight size={16} />
    </Link>
  );
};

const VersionCard = ({ version, idx }) => {
  const t = useT();
  const titleKey = `product.atlas.${version.slug.replace("atlas-", "")}.title`;
  const descKey = `product.atlas.${version.slug.replace("atlas-", "")}.desc`;
  return (
    <div
      className="reveal flex flex-col bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)] transition-colors"
      style={{ transitionDelay: `${idx * 80}ms` }}
      data-testid={`atlas-version-${version.slug}`}
    >
      <a href={version.url} target="_blank" rel="noreferrer" className="block group">
        <div className="aspect-[16/10] bg-[color:var(--gv-mist)] overflow-hidden">
          {version.image && <img src={version.image} alt={t(titleKey)} loading="lazy" className="w-full h-full object-cover" />}
        </div>
      </a>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">
          <span>{version.platform}</span>
          <span className="text-[color:var(--gv-pink)] normal-case tracking-normal font-medium">{t(`product.price.${version.price_label}`)}</span>
        </div>
        {version.free_trial_days && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-[5px] bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] text-[11px] font-medium self-start mb-3" data-testid={`trial-${version.slug}`}>
            ✨ {version.free_trial_days} jours gratuits
          </div>
        )}
        <div className="flex items-center gap-2 mb-3">
          <div className="font-display text-[22px] text-[color:var(--gv-ink)]">{t(titleKey)}</div>
          {version.positioning && <PositioningBadge positioning={version.positioning} />}
        </div>
        <p className="text-[13.5px] text-[color:var(--gv-ink)]/70 mb-5 line-clamp-3">{t(descKey)}</p>

        {version.audience?.length > 0 && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">{t("product.ideal_for")}</div>
            <div className="flex flex-wrap gap-1.5">
              {version.audience.map((a) => (
                <span key={a} className="px-2 py-0.5 rounded-[3px] bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] text-[11.5px] text-[color:var(--gv-ink)]/75">
                  {t(`audience.${a}`)}
                </span>
              ))}
            </div>
          </div>
        )}

        {version.benefits?.length > 0 && (
          <ul className="mb-5 space-y-1.5">
            {version.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-[13px] text-[color:var(--gv-ink)]/80">
                <CheckCircle2 size={14} className="mt-0.5 text-[color:var(--gv-pink)] shrink-0" />
                <span>{t(`benefit.${b}`)}</span>
              </li>
            ))}
          </ul>
        )}

        <a
          href={version.url}
          target="_blank"
          rel="noreferrer"
          className="link-arrow text-[13px] mt-auto"
        >
          {t("product.cta.visit")} <ExternalLink size={13} />
        </a>
      </div>
    </div>
  );
};

const PricingCard = ({ plan, slug }) => {
  const t = useT();
  return (
    <div
      className={`reveal bg-white border rounded-[5px] p-6 md:p-7 ${
        plan.highlighted ? "border-[color:var(--gv-pink)] ring-1 ring-[color:var(--gv-pink)]" : "border-[color:var(--gv-line)]"
      }`}
      data-testid={`pricing-${plan.plan}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-[18px] text-[color:var(--gv-ink)]">{t(`coach.plan.${plan.plan}`)}</div>
        <span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)]">{t(`coach.badge.${plan.badge}`)}</span>
      </div>
      <div className="mb-5">
        <span className="font-display text-[34px] text-[color:var(--gv-ink)]">{plan.price}</span>
        <span className="text-[14px] text-[color:var(--gv-ink)]/60 ml-1">{t(`coach.per.${plan.per}`)}</span>
        {plan.equivalent && (
          <div className="text-[12.5px] text-[color:var(--gv-ink)]/55 mt-1">{t("coach.equivalent")} : {plan.equivalent}</div>
        )}
      </div>
      <ul className="space-y-2.5 mb-6">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13.5px] text-[color:var(--gv-ink)]/80">
            <CheckCircle2 size={15} className="mt-0.5 text-[color:var(--gv-pink)]" />
            {t(`coach.feature.${f}`)}
          </li>
        ))}
      </ul>
      <ComingSoonForm slug={`${slug}-${plan.plan}`} />
    </div>
  );
};

const ProductFamilyPage = ({ forcedSlug, children }) => {
  const t = useT();
  const { slug: paramSlug } = useParams();
  const slug = forcedSlug || paramSlug;
  const product = PRODUCTS[slug];
  if (!product) return <Navigate to="/" replace />;

  const taglineKey = `product.${slug}.tagline`;
  const descKey = `product.${slug}.desc`;

  // Resolve primary CTA per product type
  let primaryCta;
  if (product.status === "coming_soon") {
    primaryCta = null;
  } else if (product.versions) {
    primaryCta = { label: t("product.cta.see_versions"), href: "#versions", external: false };
  } else if (product.url && !product.contact_sales) {
    primaryCta = { label: t("product.cta.visit"), href: product.url, external: true };
  } else if (product.cta_to) {
    primaryCta = { label: t("product.cta.discover"), href: product.cta_to, external: false, internal: true };
  } else if (product.contact_sales) {
    primaryCta = { label: t("product.cta.contact"), href: "mailto:hello@geovinum.io?subject=geoVINUM " + product.slug, external: true };
  }

  return (
    <Layout testId={`page-product-${slug}`}>
      <SEO title={`geoVINUM ${capitalise(slug)}`} description={t(taglineKey)} image={product.image} />

      <section className="pt-10 md:pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("common.home") }, { to: "/products", label: t("nav.products") }, { label: `geoVINUM ${capitalise(slug)}` }]} />
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              {product.status === "coming_soon" && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[color:var(--gv-pink)] text-white rounded-[5px] text-[10px] uppercase tracking-[0.16em] font-medium mb-4"
                     data-testid={`product-beta-badge-${slug}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                  {t("product.status.beta_program")}
                </div>
              )}
              <div className="flex items-center gap-3 mb-5">
                <span className="text-[28px]" aria-hidden>{product.emoji}</span>
                <div className="eyebrow">{t(`pillar.${product.pillar}`)} · {t(`product.type.${product.type}`)}</div>
              </div>
              <h1 className="font-display text-[42px] md:text-[60px] leading-[1.05] text-[color:var(--gv-ink)] mb-4" data-testid={`product-title-${slug}`}>
                geoVINUM <span className="italic text-[color:var(--gv-pink)]">{capitalise(slug)}</span>
              </h1>
              <p className="text-[17px] leading-[1.55] text-[color:var(--gv-ink)]/85 mb-4 italic max-w-xl">{t(taglineKey)}</p>
              <p className="text-[15px] leading-[1.7] text-[color:var(--gv-ink)]/70 mb-6 max-w-xl">{t(descKey)}</p>

              <div className="flex items-center flex-wrap gap-3 mb-7">
                <StatusPill status={product.status} />
                {product.positioning && <PositioningBadge positioning={product.positioning} />}
                {product.price_from && (
                  <span className="text-[13px] text-[color:var(--gv-ink)]/70">{t("product.starting_from")} <span className="font-medium text-[color:var(--gv-ink)]">{product.price_from}</span></span>
                )}
                {product.trial === "7d" && <span className="text-[13px] text-[color:var(--gv-pink)]">{t("product.trial_7d")}</span>}
                <button onClick={() => { const orig = document.title; document.title = `geoVINUM ${capitalise(slug)}`; setTimeout(() => { window.print(); setTimeout(() => { document.title = orig; }, 600); }, 50); }} className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)] underline" data-testid="product-download-pdf" data-print-hide="true">
                  <ArrowRight size={11} className="-rotate-90" /> {t("product.download_pdf")}
                </button>
              </div>

              {primaryCta && (
                primaryCta.internal ? (
                  <Link to={primaryCta.href} className="btn-primary" data-testid={`product-cta-${slug}`}>
                    {primaryCta.label} <ArrowRight size={16} />
                  </Link>
                ) : primaryCta.href.startsWith("#") ? (
                  <a href={primaryCta.href} className="btn-primary" data-testid={`product-cta-${slug}`}>{primaryCta.label} <ArrowRight size={16} /></a>
                ) : (
                  <a href={primaryCta.href} target="_blank" rel="noreferrer" className="btn-primary" data-testid={`product-cta-${slug}`}>
                    {primaryCta.label} <ExternalLink size={15} />
                  </a>
                )
              )}

              {product.status === "coming_soon" && (
                <div className="mt-6">
                  <ComingSoonForm slug={slug} />
                </div>
              )}
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-[6px] overflow-hidden border border-[color:var(--gv-line)] shadow-[0_40px_80px_-40px_rgba(46,80,103,0.4)]">
                <img src={product.image} alt={`geoVINUM ${slug}`} className="w-full h-[420px] md:h-[520px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning + audience + benefits (top-level for non-family products) */}
      {!product.versions && (
        <PositioningSection
          slug={slug}
          positioning={product.positioning}
          audience={product.audience}
          benefits={product.benefits}
        />
      )}

      {/* Extended marketing sections (For who / Features / Use cases / Examples) */}
      {product.sections && <ExtendedSections product={product} />}

      {/* Atlas versions */}
      {product.versions && (
        <section id="versions" className="py-20 bg-[color:var(--gv-mist)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12 reveal">
              <div className="eyebrow mb-3">{t("product.versions")}</div>
              <h2 className="font-display text-[32px] md:text-[44px] text-[color:var(--gv-ink)]">
                {t("product.versions")} <span aria-hidden>🍷</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
              {product.versions.map((v, i) => <VersionCard key={v.slug} version={v} idx={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Coach pricing */}
      {product.pricing && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12 reveal">
              <div className="eyebrow mb-3">{t("product.pricing")}</div>
              <h2 className="font-display text-[32px] md:text-[44px] text-[color:var(--gv-ink)]">{t("product.pricing")}</h2>
              {product.trial === "7d" && <p className="text-[14px] text-[color:var(--gv-pink)] mt-3">{t("product.trial_7d")}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {product.pricing.map((p) => <PricingCard key={p.plan} plan={p} slug={slug} />)}
            </div>
          </div>
        </section>
      )}

      {/* Contact sales / external */}
      {(product.contact_sales || (product.url && !product.versions)) && (
        <section className="py-20 bg-[color:var(--gv-blue)] text-white grain">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center reveal">
            <h3 className="font-display text-[28px] md:text-[40px] mb-5">
              {product.contact_sales ? t("product.cta.contact") : t("product.cta.visit")} <span aria-hidden>🍷</span>
            </h3>
            <p className="text-[14.5px] text-white/75 mb-7 max-w-lg mx-auto">{t(descKey)}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {product.url && (
                <a href={product.url} target="_blank" rel="noreferrer" className="btn-primary" data-testid={`product-visit-${slug}`}>
                  {t("product.cta.visit")} <ExternalLink size={15} />
                </a>
              )}
              {product.contact_sales && (
                <a href={`mailto:hello@geovinum.io?subject=geoVINUM ${capitalise(slug)}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-[5px] border border-white/30 text-white hover:bg-white/10 transition-colors">
                  {t("product.cta.contact")}
                </a>
              )}
            </div>
          </div>
        </section>
      )}
      {/* Extra sections injected by the consumer (rendered just before the footer) */}
      {children}
    </Layout>
  );
};

function capitalise(s) {
  if (!s) return "";
  return s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export default ProductFamilyPage;
